import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useNavTitle } from '../lib/nav'
import { Card } from '../components/ui/Card'
import { TeamScope } from '../components/TeamScope'
import { api, type CoachTeamWithGames } from '../lib/api'

export const Route = createFileRoute('/incidents')({ component: Incidents })

const LINK = 'underline text-brand-maroon hover:text-brand-red-dark'
const BUTTON = 'inline-block rounded-md border border-line bg-surface px-3 py-2 text-sm font-medium text-ink hover:bg-surface-soft'

function Mail({ to }: { to: string }) {
  return <a href={`mailto:${to}`} className={LINK}>{to}</a>
}

/** The three report wizards, signed for this team so the coach arrives with
 *  their name, email and roster already in place. */
export function ReportButtons({ team }: { team: CoachTeamWithGames }) {
  const q = useQuery({
    queryKey: ['report-links', team.externalId],
    queryFn: () => api.reportLinks(team.externalId),
  })
  if (q.isError) return <p role="alert" className="text-sm text-brand-red-dark">Couldn't load the report links. Please try again.</p>
  const links = q.data
  const items: [string, string | undefined][] = [
    ['Report an injury or incident (AYSO form)', links?.incident],
    ['Feedback about a coach', links?.coachFeedback],
    ['Feedback about a referee', links?.refereeFeedback],
  ]
  return (
    <div className="flex flex-wrap gap-2">
      {items.map(([label, href]) => href
        ? <a key={label} href={href} className={BUTTON}>{label}</a>
        : <span key={label} aria-disabled="true" className={`${BUTTON} opacity-50`}>{label}</span>)}
    </div>
  )
}

function Incidents() {
  return (
    <TeamScope
      heading={useNavTitle()}
      emptyText="No teams found for your account this season."
      hint="Pick a team to file a report."
    >
      {(team) => (
        <Card as="section">
          <h2 className="font-semibold text-ink">Incident reporting</h2>
          <div className="flex flex-col gap-3 text-sm text-ink">
            <p>Report injuries, near misses, misconduct, and other incidents, or send feedback about a coach or a referee.</p>
            <ReportButtons team={team} />
            <p>Or email: <Mail to="safety@ayso13.org" /></p>
          </div>
        </Card>
      )}
    </TeamScope>
  )
}
